import { useQuery } from "@tanstack/react-query";
import { CategoriesRepository } from "./repositories/categories-repository";

import { ClientCategories } from "./types/categories";
import { ReactNode } from "react";
import { Spin } from "@/ui/spin";
import Skeleton from "@/ui/skeleton/skeleton";
import ScrollableContainer from "@/ui/scrollable-container/scrollable-container";

export type CategoriesProps = {
  categories: ClientCategories;
};

export const useCategories = () => {
  return useQuery({
    queryKey: ["categories"],
    queryFn: CategoriesRepository.fetchCategories,
    staleTime: 600_000,
  });
};

type LoadingErrorStateProps = {
  isLoading: boolean;
  error: Error | null;
  isEmpty?: boolean;
  loadingComponent?: ReactNode;
  errorComponent?: ReactNode;
  emptyComponent?: ReactNode;
  children: ReactNode;
};

const LoadingErrorState = ({
  isLoading,
  error,
  isEmpty = false,
  loadingComponent,
  errorComponent,
  emptyComponent,
  children,
}: LoadingErrorStateProps) => {
  if (isLoading) {
    return (
      loadingComponent ?? (
        <ScrollableContainer>
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <Skeleton width={100} height={20}></Skeleton>
            <Skeleton width={100} height={20}></Skeleton>
            <Skeleton width={100} height={20}></Skeleton>
            <Skeleton width={100} height={20}></Skeleton>
            <Skeleton width={100} height={20}></Skeleton>
            <Skeleton width={100} height={20}></Skeleton>
            <Skeleton width={100} height={20}></Skeleton>
          </div>
        </ScrollableContainer>
      )
    );
  }

  if (error) {
    return (
      errorComponent ?? <div className="error">Error: {error.message}</div>
    );
  }

  if (isEmpty) {
    return emptyComponent ?? <div className="empty">No data found</div>;
  }

  return <>{children}</>;
};

export const withCategories = <P extends CategoriesProps>(
  WrappedComponent: React.ComponentType<P>,
  options?: {
    loadingComponent?: ReactNode;
    errorComponent?: ReactNode;
    emptyComponent?: ReactNode;
  },
) => {
  return (props: Omit<P, keyof CategoriesProps>) => {
    const { data: categories, isLoading, error } = useCategories();

    return (
      <LoadingErrorState
        isLoading={isLoading}
        error={error}
        isEmpty={!categories || categories.length === 0}
        loadingComponent={options?.loadingComponent}
        errorComponent={options?.errorComponent}
        emptyComponent={options?.emptyComponent}
      >
        <WrappedComponent {...(props as P)} categories={categories || []} />
      </LoadingErrorState>
    );
  };
};
